package com.ilussobsa.liveauction

import com.ilussobsa.AnimationAction
import com.ilussobsa.Auctioneer
import com.ilussobsa.AuctioneerView
import com.ilussobsa.Bid
import com.ilussobsa.LiveAuctionData
import com.ilussobsa.Resources
import com.ilussobsa.SellerTalkingPoint
import com.ilussobsa.Strings
import com.ilussobsa.UserRole
import com.ilussobsa.Vehicle
import com.ilussobsa.VehicleRelationship
import com.ilussobsa._id
import com.ilussobsa.at
import com.ilussobsa.auctioneer
import com.ilussobsa.nullUuid
import com.ilussobsa.orderingValue
import com.ilussobsa.price
import com.ilussobsa.renderPriceInDollars
import com.ilussobsa.reserve
import com.ilussobsa.sdk.CachedApi2
import com.ilussobsa.sdk.currentSession
import com.ilussobsa.sdk.currentUser
import com.ilussobsa.sdk.currentUserSession
import com.ilussobsa.sdk.isSeller
import com.ilussobsa.sdk.managesAuctions
import com.ilussobsa.user
import com.ilussobsa.utils.AuctionCTASemantic
import com.ilussobsa.utils.BadgeSemantic
import com.ilussobsa.utils.EventResources
import com.ilussobsa.utils.FullScreenAuction
import com.ilussobsa.utils.HighlightSemantic
import com.ilussobsa.utils.InactiveSemantic
import com.ilussobsa.utils.LiveSemantic
import com.ilussobsa.utils.audioPack
import com.ilussobsa.utils.liveAuction
import com.ilussobsa.utils.sell
import com.ilussobsa.utils.winGraphic
import com.ilussobsa.vehicle
import com.ilussobsa.views.VerificationDialog
import com.ilussobsa.views.loadingDots
import com.ilussobsa.views.vehicleInfo
import com.lightningkite.UUID
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.SoundEffectPool
import com.lightningkite.kiteui.backgroundAudio
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.ButtonSemantic
import com.lightningkite.kiteui.models.H1Semantic
import com.lightningkite.kiteui.models.HeaderSemantic
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.ImageScaleType
import com.lightningkite.kiteui.models.ScreenTransition
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogPageNavigator
import com.lightningkite.kiteui.navigation.pageNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atBottomEnd
import com.lightningkite.kiteui.views.atBottomStart
import com.lightningkite.kiteui.views.card
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.danger
import com.lightningkite.kiteui.views.direct.align
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.frame
import com.lightningkite.kiteui.views.direct.h1
import com.lightningkite.kiteui.views.direct.h3
import com.lightningkite.kiteui.views.direct.icon
import com.lightningkite.kiteui.views.direct.image
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.recyclerView
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.rowCollapsingToColumn
import com.lightningkite.kiteui.views.direct.scrolling
import com.lightningkite.kiteui.views.direct.shownWhen
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.subtext
import com.lightningkite.kiteui.views.direct.swapView
import com.lightningkite.kiteui.views.direct.swapping
import com.lightningkite.kiteui.views.direct.text
import com.lightningkite.kiteui.views.direct.toggleButton
import com.lightningkite.kiteui.views.direct.weight
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.emphasized
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.important
import com.lightningkite.kiteui.views.l2.children
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.and
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq
import com.lightningkite.lightningdb.modification
import com.lightningkite.lightningdb.sort
import com.lightningkite.now
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import kotlin.collections.plus
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.math.absoluteValue
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime

@Routable("live-auction/{id}")
class LiveAuctionScreen(val id: UUID) : Page, FullScreenAuction {

    private val smallScreenBreakpoint = 1300.dp
    private val isSmallScreen = remember { AppState.windowInfo().width <= smallScreenBreakpoint }

    val enableAuctioneer = Signal(true)
    val forceAuctioneerAlways = Signal(false)

    override fun ViewWriter.render() = frame {
        sizeConstraints(width = 1700.dp) - align(Align.Center, Align.Stretch) - col {
            val liveAuctionData = remember<LiveAuctionData?> { currentSession().liveAuctionData.watch(id)() }
            val live = rememberSuspending {
                liveAuctionData() ?: createDummyLiveActionData(currentSession(), id)
            }
            val vehicle = remember { live()?.currentVehicle?.let { currentSession().vehicle[it]() } }
            val vehicleRelationship = remember {
                val userId = currentUser()?._id ?: return@remember null
                val vehicleId = vehicle()?._id ?: return@remember null
                currentSession().vehicleRelationship.query(Query(limit = 1) {
                    it._id.user.eq(userId) and it._id.vehicle.eq(vehicleId)
                })().firstOrNull()
            }
            val currentLotBids = remember {
                currentSession().bid.watch(
                    Query(
                        orderBy = sort { it.price.ascending(); it.at.ascending() },
                        limit = 1000,
                        condition = condition { it.vehicle eq live.awaitNotNull().currentVehicle })
                )().distinctBy { it.price }
            }
            val talkingPoints = remember {
                currentSession().sellerTalkingPoint.watch(
                    Query(
                        orderBy = sort { it.at.ascending() },
                        limit = 1000,
                        condition = condition { it.vehicle eq live.awaitNotNull().currentVehicle })
                )()
            }
            val saleHistory = remember { (talkingPoints() + currentLotBids()).sortedBy { it.at } }

            var auctioneerView: AuctioneerView? = null
            val presenter = object : AuctionPresenter {
                val currentAnimation = Signal(AnimationAction.IDLE)
                var disableAudio = true
                override fun playAnimation(action: AnimationAction) {
                    println("Playing animation: $action")
                    currentAnimation.value = action
                    auctioneerView?.animationAction = action
                }

                val pool = SoundEffectPool()
                override suspend fun playAudio(event: EventResources, withGavel: Boolean) {
                    if (disableAudio) return
                    try {
                        if (withGavel) {
                            pool.play(Resources.auctioneerGavel)
                        }
                        pool.play(event.audio.randomOrNull() ?: return)
                    } catch (t: Throwable) {
                        // whatever.
                        println("Note: Could not play a sound. ${t.message}")
                    }
                }

                override fun performPoke(poke: LocalPoke) {
                    // TODO
                }
            }
            launch {
                delay(1000)
                presenter.disableAudio = false
            }
            val dontShowLoading = CoroutineScope(coroutineContext + object : StatusListener {
                override fun loading(readable: Reactive<*>) {}
            })
            val controller = AuctionController(
                live,
                currentLotBids,
                Constant(Auctioneer.Recording1.audioPack()),
                presenter,
                dontShowLoading
            )

            backgroundAudio(Resources.auctioneerCrowdBackground, 0.4f) {
                pageNavigator.currentPage() == this@LiveAuctionScreen && controller.auctionRoomOpen()
            }

            expanding - scrolling - col {
                button {
                    ::shown { liveAuctionData() == null && managesAuctions() }
                    onClick {
                        if (liveAuctionData() == null && managesAuctions()) {
                            currentSession().uncached.liveAuctionData.start(id)
                        }
                    }
                    text(Strings.startTheAuction)
                }

                rowCollapsingToColumn(smallScreenBreakpoint) {
                    sizeConstraints(width = 500.dp, height = 450.dp) - frame {
                        padding = 0.px
                        ::shown { !isSmallScreen() }
                        reactive {
                            clearChildren()
                            if(forceAuctioneerAlways() || controller.auctionRoomOpen()) {
                                if (enableAuctioneer()) {
                                    auctioneer {
                                        auctioneerView = this
                                    }
                                } else {
                                    centered - text("Visual auctioneer disabled")
                                }
                            } else {
                                image {
                                    // TODO: Regenerate this photo without text and render the text dynamically so that font theming works
                                    scaleType = ImageScaleType.Crop
                                    source = Resources.auctioneerWaiting
                                }
                            }
//                            atBottomEnd - important - toggleButton {
//                                text("Toggle Auctioneer")
//                                checked bind enableAuctioneer
//                            }
//                            atBottomStart - important - toggleButton {
//                                text("Force Auctioneer")
//                                checked bind forceAuctioneerAlways
//                            }
                        }
                    }
                    sizeConstraints(height = 450.dp) - weight(0.65f) - askingPriceCard(
                        controller,
                        vehicle,
                        vehicleRelationship,
                    )
                    sizeConstraints(height = 450.dp) - weight(0.35f) - activityCard(saleHistory)
                }

                card - vehicleInfo(vehicle, immutable = true, goto = {})
            }
            shownWhen { isSmallScreen() && controller.liveAuctionData() != null } - frame {
                actionButtons(controller)
            }
        }
    }

    private val askingPriceFormat = LocalDateTime.Format {
        amPmHour()
        char(':')
        minute()
        char(' ')
        amPmMarker("AM", "PM")
        chars(" PST – ")
        monthNumber()
        char('/')
        dayOfMonth()
        char('/')
        yearTwoDigits(2000)
    }

    private fun ViewWriter.askingPriceCard(
        controller: AuctionController,
        vehicle: Reactive<Vehicle?>,
        vehicleRelationship: Reactive<VehicleRelationship?>,
    ) = card - col {
        with(controller) {
            val goingStatus = remember {
                liveAuctionData()?.finalLaneTimeout?.let {
                    val countdown = it - nowBySecond() - LiveAuctionData.lagAccounting
                    when {
                        countdown < LiveAuctionData.goingDuration -> 2
                        countdown < LiveAuctionData.goingDuration * 2 -> 1
                        else -> null
                    }
                }
            }

            dynamicTheme {
                InactiveSemantic.takeIf { !isLive() }
            }

            row {
                centered - HighlightSemantic.onNext - icon {
                    source = Icon.liveAuction
                }
                centered - LiveSemantic.onNext - BadgeSemantic.onNext - text {
                    ::content { if (isLive()) Strings.live else Strings.startingSoon }
                }
                expanding - space()
                centered - text {
                    ::content {
                        if (isLive()) {
                            Strings.timeToBid
                        } else {
                            liveAuctionData()?.laneStartedAt?.toLocalDateTime(TimeZone.currentSystemDefault())
                                ?.format(askingPriceFormat) ?: "–"
                        }
                    }
                }
                centered - HighlightSemantic.onNext - text {
                    ::shown { isLive() }
                    ::content {
                        val countdown = liveAuctionData()?.timeout?.let { it - nowBySecond() } ?: 0.seconds
                        "${countdown.inWholeSeconds.coerceAtLeast(0)} sec"
                    }
                }
            }
            h1 {
                ::content { vehicle()?.let { "${it.make} ${it.model}" } ?: "–" }
            }
            space()
            shownWhen { biddingOpen() } - row {
                centered - emphasized - text(Strings.askingPrice)
                space()
                shownWhen { vehicleRelationship()?.autobid != null } - centered -
                        subtext(Strings.yourProxyBid)
                shownWhen { vehicleRelationship()?.autobid != null } - centered -
                        text { ::content { vehicleRelationship()?.autobid?.renderPriceInDollars() ?: "" } }
            }

            shownWhen { !isLive() } - loadingDots()
            shownWhen { biddingOpen() } - AuctionCTASemantic.onNext -
                    splitFlapDisplay(remember { liveAuctionData()?.asking?.renderPriceInDollars() ?: "$–.––" })

            expanding - space()

            sizeConstraints(height = 110.dp) - swapView {
                swapping(
                    transition = {
                        when (it) {
                            true -> ScreenTransition.Pop
                            false -> ScreenTransition.Fade
                        }
                    },
                    current = { liveAuctionData()?.completion?.winner == currentUser()?._id && currentUser()?._id != null },
                    views = {
                        when (it) {
                            true -> image { source = Resources.winGraphic }
                            false -> col {
                                HighlightSemantic.onNext - HeaderSemantic.onNext - H1Semantic.onNext -
                                        splitFlapDisplay(
                                            remember {
                                                when (goingStatus()) {
                                                    2 -> Strings.goingTwice
                                                    1 -> Strings.goingOnce
                                                    else -> liveAuctionData()?.completion?.let { if (liveAuctionData()?.reserveMet == true) "SOLD" else Strings.noSale }
                                                        ?: ""
                                                }
                                            },
                                            Align.Center
                                        )
                                shownWhen { !isSmallScreen() && controller.liveAuctionData() != null } -
                                        actionButtons(controller)
                            }
                        }
                    }
                )
            }
        }
    }

    private fun ViewWriter.actionButtons(controller: AuctionController) = col {
        authorizeSaleButton(controller)
        bidButton(controller)
    }

    private fun Duration.humanReadableCountdown() = if (this > 10.minutes) {
        this.toComponents { days, hours, minutes, seconds, nanoseconds ->
            fun Number.addUnit(unit: String): String {
                val unit = if (this != 1) unit else unit.dropLast(1)
                return "${toString()} $unit"
            }

            val daysString = days.takeIf { it > 0 }?.addUnit("days")
            val hoursString = hours.takeIf { it > 0 }?.addUnit("hours")
            val minutesString = minutes.addUnit("minutes")
            listOfNotNull(daysString, hoursString, minutesString).joinToString(" ")
        }
    } else {
        this.toComponents { minutes, seconds, nanoseconds ->
            val min = minutes.absoluteValue.toString().padStart(2, '0')
            val sec = seconds.absoluteValue.toString().padStart(2, '0')
            "$min:$sec"
        }
    }

    private fun ViewWriter.authorizeSaleButton(controller: AuctionController) =
        ButtonSemantic.onNext - important - danger - button {
            align(Align.Center, Align.Center) - text {
                ::content {
                    with(controller) {
                        val countdown = countdown()
                        if (countdown != null && countdown > 0.seconds) {
                            Strings.auctionStartsIn(countdown.humanReadableCountdown())
                        } else if (liveAuctionData.awaitNotNull().reserveMet) {
                            Strings.reserveMet
                        } else {
                            Strings.authorizeSale
                        }
                    }
                }
            }
            ::shown {
                isSeller()
            }
            ::enabled {
                with(controller) {
                    biddingOpen() && liveAuctionData()?.let { it.completion == null && !it.reserveMet }
                            ?: false
                }
            }
            onClick {
                val vehicle = controller.currentLotId() ?: return@onClick
                currentUserSession().vehicle[vehicle].modify(modification {
                    it.reserve assign null
                })
            }
        }

    private fun ViewWriter.bidButton(controller: AuctionController) =
        LiveSemantic.plus(ButtonSemantic).onNext - important - button {
            ::enabled { controller.biddingOpen() }
            ::shown { !isSeller() }

            this.action = Action(Strings.placeABid, Icon.sell) {
                val buyer = currentUser()?.takeIf { it.role in UserRole.canInteractInAuction }?._id ?: run {
                    if (dialogPageNavigator.currentPage() !is VerificationDialog) {
                        dialogPageNavigator.navigate(VerificationDialog())
                    }
                    return@Action
                }

                val weHaveTopBid = controller.liveAuctionData()?.winningBid?.buyer == buyer
                if (weHaveTopBid) return@Action

                val vehicle = controller.liveAuctionData()?.currentVehicle ?: return@Action
                val price = controller.liveAuctionData()?.asking ?: return@Action
                currentUserSession().bid.add(
                    Bid(
                        vehicle = vehicle,
                        buyer = buyer,
                        price = price,
                    )
                )
            }

            align(Align.Center, Align.Center) - text {
                ::content {
                    with(controller) {
                        val weHaveTopBid =
                            liveAuctionData()?.winningBid?.buyer == currentUser()?._id && currentUser()?._id != null
                        val countdown = countdown()
                        if (countdown != null && countdown > 0.seconds) {
                            Strings.startBiddingIn(countdown.humanReadableCountdown())
                        } else if (weHaveTopBid) {
                            Strings.youHaveTheTopBid
                        } else {
                            "Bid ${liveAuctionData()?.asking?.renderPriceInDollars() ?: "$–.––"}"
                        }
                    }
                }
            }
        }

    @OptIn(ExperimentalEncodingApi::class)
    private fun ViewWriter.activityCard(saleHistory: Reactive<List<Any>>) = card - col {
        HighlightSemantic.onNext - h3(Strings.activity)
        expanding - recyclerView {
            children(saleHistory, id = {
                when (it) {
                    is Bid -> it._id.toString()
                    is SellerTalkingPoint -> it._id.toString()
                    is LocalPoke -> it.at.toString()
                    else -> it.hashCode().toString()
                }
            }) {
                sizeConstraints(height = 80.dp) - col {
                    subtext {
                        reactive {
                            content = when (val item = it()) {
                                is Bid -> {
                                    val user = currentSession.awaitNotNull().user[item.buyer]()
                                    user?.biddingDisplayName ?: "-"
                                }

                                is SellerTalkingPoint -> Strings.sellerSays
                                else -> ""
                            }.also { exists = it.isNotEmpty() }
                        }
                    }
                    text {
                        ::content {
                            when (val item = it()) {
                                is Bid -> "bid ${item.price.renderPriceInDollars()}"
                                is SellerTalkingPoint -> item.message
                                is LocalPoke -> item.message
                                else -> ""
                            }
                        }
                    }
                }
            }
            reactiveSuspending {
                val last = saleHistory.await().lastIndex
                delay(100)
                scrollToIndex(last, Align.End)
            }
        }
    }
}

/**
 * A swapView that switches between two text views, allowing for transitions every time text changes
 */
private fun ViewWriter.splitFlapDisplay(content: Reactive<String>, align: Align? = null) = swapView {
    val currentSlot = Signal(true)
    val slotA = Signal("")
    val slotB = Signal("")

    var abToggle = false
    reactive {
        abToggle = !abToggle
        if (abToggle) {
            slotA.value = content()
        } else {
            slotB.value = content()
        }
        currentSlot.value = abToggle
    }

    swapping(
        transition = { ScreenTransition.PullDown },
        current = { currentSlot() },
        views = {
            when (it) {
                true -> text { ::content { slotA() }; align?.let { this.align = it } }
                false -> text { ::content { slotB() }; align?.let { this.align = it } }
            }
        }
    )
}

/**
 * For auctions that have not yet started on the server (i.e. a LiveAuctionData entry has not been created for an
 * AuctionLane), we can create a transient LiveAuctionData instance so that we can still render the live auction page
 */
private suspend fun createDummyLiveActionData(session: CachedApi2, id: UUID): LiveAuctionData? {
    val auction = session.auctionLane[id]()?.takeIf { it.scheduledStartInstant > now() } ?: return null
    val firstVehicle = session.vehicle.query(
        Query(
        limit = 1,
        condition = auction.runlist,
        orderBy = sort { it.orderingValue.ascending() }
    ))().firstOrNull()
    println("First vehicle: $firstVehicle")
    return LiveAuctionData(
        _id = auction._id,
        currentVehicle = firstVehicle?._id ?: nullUuid,
        laneStartedAt = auction.scheduledStartInstant,
    )
}